import { useEffect, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { DeveloperGuide, GUIDE_SECTIONS } from '@/features/developers/guide';
import { ApiReference } from '@/features/developers/reference';
import { cn } from '@/lib/cn';
import { CardSkeleton } from '@/components/ui/feedback';

const searchSchema = z.object({
  view: z.enum(['guide', 'reference']).optional(),
});

export const Route = createFileRoute('/_app/developers')({
  // The shape this page takes, rather than a search box over a table for
  // every route: the screen must not rearrange itself once the data lands.
  pendingComponent: () => <CardSkeleton lines={6} />,
  validateSearch: searchSchema,
  component: DevelopersPage,
});

/**
 * Two views: the guide, in the order a developer does things, and the endpoint reference
 * generated from the contract. The guide has a table of contents that follows the reader.
 *
 * Which view is showing comes from ?view=, and the control that changes it sits in the
 * header beside the breadcrumb, where every other page with views puts its own.
 */
function DevelopersPage() {
  const { view } = Route.useSearch();
  const current = view ?? 'guide';

  return (
    <div className="flex flex-col gap-5">
      {current === 'reference' ? (
        <ApiReference />
      ) : (
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[220px_minmax(0,1fr)]">
          <OnThisPage />
          <DeveloperGuide />
        </div>
      )}
    </div>
  );
}

/** The table of contents. Highlights the section under the reader as they scroll. */
function OnThisPage() {
  const [active, setActive] = useState<string>(GUIDE_SECTIONS[0]?.id ?? '');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: '-80px 0px -70% 0px', threshold: 0 },
    );
    for (const section of GUIDE_SECTIONS) {
      const node = document.getElementById(section.id);
      if (node) observer.observe(node);
    }
    return () => observer.disconnect();
  }, []);

  return (
    <nav aria-label="On this page" className="xl:sticky xl:top-20 xl:self-start">
      <p className="mb-2 text-xs font-semibold tracking-wide text-muted-foreground uppercase">On this page</p>
      <ol className="flex flex-col gap-0.5 border-l border-[color:var(--ts-border-soft)]">
        {GUIDE_SECTIONS.map((section) => (
          <li key={section.id}>
            <a
              href={`#${section.id}`}
              aria-current={active === section.id ? 'location' : undefined}
              className={cn(
                '-ml-px block border-l-2 py-1 pl-3 text-base transition-colors duration-150 ease-out',
                active === section.id
                  ? 'border-primary font-medium text-primary'
                  : 'border-transparent text-gray-3 hover:border-[color:var(--ts-border)] hover:text-primary',
              )}
            >
              {section.title}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}
